import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Sparkles, ExternalLink } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { CITIES } from "@/cityConfig";
import { API, BUTTON_NAMES, SOURCE_NAMES, AI_SOURCES } from "./constants";

const LANG_NAMES = { ru: "Русский", en: "English", tr: "Türkçe" };
const DEVICE_NAMES = { desktop: "Компьютер", mobile: "Мобильный", tablet: "Планшет" };

function BarList({ title, items, label = (k) => k, total, testId, mono = false }) {
  const max = items[0]?.count || 1;
  return (
    <div data-testid={testId}>
      <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 font-sans">{title}</div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-400">Нет данных</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => (
            <div key={it.key}>
              <div className="flex items-center justify-between gap-3 mb-1">
                <span className={`text-sm text-gray-700 truncate ${mono ? "font-mono" : "font-medium"}`} title={it.key}>
                  {label(it.key)}
                </span>
                <span className="text-sm font-bold text-gray-900 shrink-0">
                  {it.count}
                  {total ? <span className="text-gray-400 font-medium ml-1.5">{Math.round((it.count / total) * 100)}%</span> : null}
                </span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-violet-400 to-indigo-500 rounded-full" style={{ width: `${(it.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Chips({ title, items, label = (k) => k, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 font-sans">{title}</div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-400">Нет данных</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((it) => (
            <span key={it.key} className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-full px-3 py-1 text-sm text-gray-700">
              {label(it.key)} <b className="text-gray-900">{it.count}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function SourceDrilldown({ password, source, period, date, city, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const rootRef = useRef(null);

  useEffect(() => {
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [source]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { password, source, period };
    if (date) params.date = date;
    if (city) params.city = city;
    axios
      .get(`${API}/admin/analytics/source`, { params })
      .then((r) => alive && setD(r.data))
      .catch(() => alive && setD(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [password, source, period, date, city]);

  const name = SOURCE_NAMES[source] || source;
  const isAi = AI_SOURCES.includes(source);
  const conv = d && d.sessions > 0 ? Math.round((d.click_sessions / d.sessions) * 100) : 0;

  return (
    <div ref={rootRef} className="bg-white rounded-2xl border-2 border-indigo-100 p-5 animate-fade-in scroll-mt-4" data-testid="source-drilldown">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-gray-900 text-lg">Источник: {name}</h3>
            {isAi && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-violet-50 text-violet-600 border border-violet-100 rounded-full px-2.5 py-0.5">
                <Sparkles className="w-3 h-3" /> ИИ-ассистент
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {isAi
              ? "ChatGPT и другие ИИ передают только домен — текст запроса и диалог не видны никому. Ниже — куда именно пришли люди и что делали."
              : "Куда именно пришли посетители из этого источника и что делали на сайте."}
          </p>
        </div>
        <button onClick={onClose} className="p-2 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors" data-testid="source-drilldown-close" aria-label="Закрыть">
          <X className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center">
          <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : !d ? (
        <p className="text-sm text-gray-400">Не удалось загрузить детализацию</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              ["Просмотры", d.total_views],
              ["Сессии", d.sessions],
              ["Клики WhatsApp/Telegram", d.total_clicks],
              ["Сессий с кликом", `${d.click_sessions} · ${conv}%`],
            ].map(([l, v]) => (
              <div key={l} className="bg-gray-50 rounded-xl p-3" data-testid={`drill-stat-${l}`}>
                <div className="text-xl font-bold text-gray-900">{typeof v === "number" ? v.toLocaleString() : v}</div>
                <div className="text-[11px] text-gray-500 font-medium mt-0.5">{l}</div>
              </div>
            ))}
          </div>

          {d.daily.length > 0 && (
            <div className="mb-6" data-testid="drill-daily">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 font-sans">{date ? "Заходы по часам (UTC)" : "Заходы по дням"}</div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={d.daily}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => (date ? v : v.slice(5))} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} width={28} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }}
                    labelFormatter={(v) => (date ? `${v} UTC` : new Date(v).toLocaleDateString("ru-RU", { day: "numeric", month: "long" }))}
                    formatter={(v) => [v, "Просмотры"]}
                  />
                  <Bar dataKey="views" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BarList title="Посадочные страницы" items={d.pages} total={d.total_views} testId="drill-pages" mono />
            <div className="space-y-6">
              <BarList
                title="Города"
                items={d.cities}
                total={d.total_views}
                label={(k) => CITIES[k]?.name || k}
                testId="drill-cities"
              />
              <Chips title="Языки" items={d.langs} label={(k) => LANG_NAMES[k] || k} testId="drill-langs" />
              <Chips title="Устройства" items={d.devices} label={(k) => DEVICE_NAMES[k] || k} testId="drill-devices" />
              <Chips title="Клики по кнопкам" items={d.buttons} label={(k) => BUTTON_NAMES[k] || k} testId="drill-buttons" />
              {d.click_pages.length > 0 && (
                <BarList title="Где нажимали «написать»" items={d.click_pages} total={d.total_clicks} testId="drill-click-pages" mono />
              )}
            </div>
          </div>

          {d.referrers.length > 0 && (
            <div className="mt-6" data-testid="drill-referrers">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 font-sans">Полные адреса перехода (referrer)</div>
              <div className="space-y-1.5">
                {d.referrers.map((r) => (
                  <div key={r.key} className="flex items-center justify-between gap-3 text-xs bg-gray-50 rounded-lg px-3 py-2">
                    <a href={r.key} target="_blank" rel="noopener noreferrer" className="font-mono text-gray-600 hover:text-indigo-600 truncate inline-flex items-center gap-1" title={r.key}>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                      <span className="truncate">{r.key}</span>
                    </a>
                    <b className="text-gray-900 shrink-0">{r.count}</b>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
